import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Switch,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  useTheme,
  Divider,
  FormControlLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

interface ScheduleItem {
  id: string;
  frequency: "daily" | "weekly";
  time: string; // e.g. "03:30 AM"
  dayOfWeek?: string; // for weekly
  enabled: boolean;
}

const daysOfWeek = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
];

const ReportSchedule: React.FC<{
  reportData: any;
  setReportData: any;
  onSave: () => void;
}> = ({ reportData, setReportData, onSave }) => {
  const theme = useTheme();
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<ScheduleItem | null>(null);

  const handleAdd = () => {
    setEditingItem({
      id: "id-" + Math.random().toString(36).substr(2, 9) + "-" + Date.now(),
      frequency: "daily",
      time: "03:00 AM",
      enabled: true,
    });
    setShowDialog(true);
  };

  const handleEdit = (item: ScheduleItem) => {
    setEditingItem(item);
    setShowDialog(true);
  };

  const handleDelete = (id: string) => {
    const updated = (reportData.schedule || []).filter((s: ScheduleItem) => s.id !== id);
    setReportData({ ...reportData, schedule: updated });
  };

  const handleToggle = (id: string) => {
    const updated = (reportData.schedule || []).map((s: ScheduleItem) =>
      s.id === id ? { ...s, enabled: !s.enabled } : s
    );
    setReportData({ ...reportData, schedule: updated });
  };

  const handleDialogSave = () => {
    const updated = [...(reportData.schedule || [])];
    const index = updated.findIndex(s => s.id === editingItem!.id);
    if (index > -1) updated[index] = editingItem!;
    else updated.push(editingItem!);
    setReportData({ ...reportData, schedule: updated });
    setShowDialog(false);
    setEditingItem(null);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 4: Schedule Report Runs
      </Typography>

      {/* List of existing schedules */}
      {(reportData.schedule || []).map((item: ScheduleItem) => (
        <Paper
          key={item.id}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            mb: 1,
            borderLeft: `4px solid ${item.enabled ? theme.palette.primary.main : theme.palette.grey[500]}`,
          }}
        >
          <Box>
            <Typography fontWeight={500}>
              {item.frequency === "daily"
                ? `Daily at ${item.time}`
                : `Every ${item.dayOfWeek} at ${item.time}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.enabled ? "Enabled" : "Disabled"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Switch checked={item.enabled} onChange={() => handleToggle(item.id)} />
            <IconButton onClick={() => handleEdit(item)}><EditIcon /></IconButton>
            <IconButton onClick={() => handleDelete(item.id)}><DeleteIcon color="error" /></IconButton>
          </Box>
        </Paper>
      ))}

      {/* Add schedule button */}
      <Box mt={2}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Schedule
        </Button>
      </Box>

      {/* Save report */}
      <Box mt={4}>
        <Divider sx={{ mb: 2 }} />
        <Button variant="contained" color="primary" onClick={onSave}>
          Save Report
        </Button>
      </Box>

      {/* Dialog for Add/Edit */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingItem?.id ? "Edit Schedule" : "New Schedule"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Frequency</InputLabel>
            <Select
              value={editingItem?.frequency || "daily"}
              onChange={(e) =>
                setEditingItem({ ...editingItem!, frequency: e.target.value as "daily" | "weekly" })
              }
              label="Frequency"
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
            </Select>
          </FormControl>

          {editingItem?.frequency === "weekly" && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={editingItem.dayOfWeek || "Monday"}
                onChange={(e) =>
                  setEditingItem({ ...editingItem!, dayOfWeek: e.target.value })
                }
                label="Day of Week"
              >
                {daysOfWeek.map(day => (
                  <MenuItem key={day} value={day}>{day}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Time"
            fullWidth
            type="time"
            value={editingItem?.time || "03:00"}
            onChange={(e) =>
              setEditingItem({ ...editingItem!, time: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={editingItem?.enabled || false}
                onChange={ () =>{
                    if(editingItem!=null) setEditingItem({ ...editingItem!, enabled: !editingItem.enabled });
                }
                  
                }
              />
            }
            label="Enabled"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportSchedule;
